import { FC, useState } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Control, Controller, FieldValues } from "react-hook-form";
import moment from "moment";

moment.updateLocale("en", {
  week: { dow: 1 }, // 'dow: 1' makes Monday the first day of the week
});

interface DatePickerProps {
  restProps?: any;
  label?: string;
  id: string;
  errors: Record<string, any>;
  disableFuture?: boolean;
  disablePast?: boolean;
  control: Control<FieldValues>;
  onMonthChange?: (month: any) => void;
  shouldBeOpen?: boolean;
  shouldDisableDate?: (date: any) => boolean;
}

const DatePicker: FC<DatePickerProps> = ({
  restProps,
  label,
  id,
  disableFuture = false,
  disablePast = false,
  errors,
  control,
  onMonthChange,
  shouldBeOpen = false,
  shouldDisableDate,
}) => {
  const [isOpen, setIsOpen] = useState(shouldBeOpen);
  const [validError, setValidError] = useState("");

  const handleDateChange = (field: any, date: any) => {
    const formattedDate = date ? moment(date).format("YYYY-MM-DD") : null;
    field.onChange(formattedDate); // Ensure date is in the correct format
    setIsOpen(false);
  };

  const handleError = (error: string | null) => {
    if (error === "shouldDisableDate") {
      setValidError(
        "This date doesn't have a slot available. Please choose another date.",
      );
    } else {
      setValidError("");
    }
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {label && <label htmlFor={id}>{label}</label>}

      <Controller
        control={control}
        name={id}
        render={({ field }) => {
          return (
            <DesktopDatePicker
              shouldDisableDate={shouldDisableDate}
              onChange={(date) => handleDateChange(field, date)}
              onMonthChange={onMonthChange}
              onError={handleError}
              ref={restProps?.ref}
              name={id}
              className="w-full bg-white rounded-md"
              disableFuture={disableFuture}
              disablePast={disablePast}
              // readOnly
              slotProps={{
                ...{
                  textField: {
                    onClick: () => setIsOpen(true),
                    inputProps: { readOnly: true },
                  },
                  openPickerButton: {
                    onClick: () => setIsOpen(true),
                    className:
                      "bg-white border-l cursor-pointer hover:bg-gray-300",
                  },
                },
              }}
              format="D MMM YYYY"
              sx={{
                button: {
                  color: "rgba(0, 0, 0, 0.54)",
                  backgroundColor: "white",
                  ":hover": {
                    backgroundColor: "#E0E0E0",
                    color: "rgba(0, 0, 0, 0.54)",
                  },
                },
              }}
              value={moment(field.value)} // Ensure field.value is correctly handled
              closeOnSelect
              onClose={() => setIsOpen(false)}
              open={isOpen}
            />
          );
        }}
      />

      <div className="text-sm text-red-600">
        {errors[id]?.message || validError}
      </div>
    </div>
  );
};

export default DatePicker;
